import React from "react";

import ReceiptIcon from "@mui/icons-material/Receipt";
import PostAddIcon from "@mui/icons-material/PostAdd";
import DonutSmallIcon from "@mui/icons-material/DonutSmall";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CurrencyPoundIcon from "@mui/icons-material/CurrencyPound";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import ChromeReaderModeIcon from "@mui/icons-material/ChromeReaderMode";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import AccessibilityIcon from "@mui/icons-material/Accessibility";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import TableChartIcon from "@mui/icons-material/TableChart";
import BubbleChartIcon from "@mui/icons-material/BubbleChart";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import MenuBook from "@mui/icons-material/MenuBook";
import InventoryIcon from "@mui/icons-material/Inventory";
import InsertChartIcon from "@mui/icons-material/InsertChart";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import EuroIcon from "@mui/icons-material/Euro";
import HistoryIcon from "@mui/icons-material/History";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import SummarizeIcon from "@mui/icons-material/Summarize";
import SmsFailedIcon from "@mui/icons-material/SmsFailed";
import TodayIcon from "@mui/icons-material/Today";
import ScienceIcon from "@mui/icons-material/Science";
import PeopleIcon from "@mui/icons-material/People";

import useCan from "./useCan";
import FlaskIcon from "../../assets/icons/Flask.icon";

export default function useReportList(): Record<string, Record<string, any>[]> {
  const reports = {
    billing: [
      {
        label: "Sales Report",
        name: "salesReport",
        permission: "report:salesReport",
        can: useCan("report:salesReport", {}),
        path: "/reports/financial/salesReport",
        icon: <ChromeReaderModeIcon />,
        description: "Overview of sales transactions and revenue metrics."
      },
      {
        label: "SP and Referral Commission Report",
        name: "commissionReport",
        permission: "report:feesAndReferralReport",
        can: useCan("report:feesAndReferralReport", {}),
        path: "/reports/financial/commissionReport",
        icon: <MonetizationOnIcon />,
        description: "Details of service provider fees and referer commissions."
      },
      {
        label: "Sales by Service Report",
        name: "salesByServiceReport",
        permission: "report:salesByServiceReport",
        can: useCan("report:salesByServiceReport", {}),
        path: "/reports/financial/salesByServiceReport",
        icon: <PostAddIcon />,
        description: "Breakdown of revenue by service categories."
      },
      {
        label: "Due Report",
        name: "dueReport",
        permission: "report:dueReport",
        can: useCan("report:dueReport", {}),
        path: "/reports/financial/dueReport",
        icon: <AssignmentReturnIcon />,
        description: "Outstanding payments and dues from clients."
      },
      {
        label: "Receipt Report",
        name: "receiptReport",
        permission: "report:receiptReport",
        can: useCan("report:receiptReport", {}),
        path: "/reports/financial/receiptReport",
        icon: <ReceiptIcon />,
        description: "Record of all payments payment receipts issued."
      },
      {
        label: "Client Ledger Report",
        name: "clientLedgerReport",
        permission: "allow",
        can: true,
        path: "/reports/financial/clientLedger",
        icon: <CurrencyPoundIcon />,
        description: "Detailed ledger of client transactions."
      },
      {
        label: "Service Provider Charges Report",
        name: "serviceProviderChargesReport",
        permission: "report:serviceProviderChargesReport",
        can: useCan("report:serviceProviderChargesReport", {}),
        path: "/reports/financial/serviceProviderChargesReport",
        icon: <DonutSmallIcon />,
        description: "Charges and payments to service providers."
      },
      {
        label: "IPD Bills report",
        name: "ipdBillsReport",
        permission: "allow",
        can: true,
        path: "/reports/financial/ipdBillsReport",
        icon: <ReceiptLongIcon />,
        description: "Detailed report of in-patient department billing."
      }
    ],
    account: [
      {
        label: "Trial Balance",
        name: "trialBalanceReport",
        permission: "allow",
        can: true,
        path: "/reports/account/trialBalance",
        icon: <DonutSmallIcon />,
        description: "A summary of all ledger accounts with their debit and credit balances."
      },
      {
        label: "General Ledger",
        name: "generalLedgerReport",
        permission: "allow",
        can: true,
        path: "/reports/account/generalLedger",
        icon: <CreditScoreIcon />,
        description: "Detailed transactions of all accounts over a specific period."
      },
      {
        label: "Customer Report",
        name: "customerReport",
        permission: "allow",
        can: true,
        path: "/reports/account/customerReport",
        icon: <AccessibilityIcon />,
        description: "Shows outstanding balances and transactions for each customer."
      },
      {
        label: "Vendor Report",
        name: "vendorReport",
        permission: "allow",
        can: true,
        path: "/reports/account/vendorReport",
        icon: <AddBusinessIcon />,
        description: "Tracks payables and purchase history for each vendor."
      },
      {
        label: "Bank Reconciliation",
        name: "bankReconciliationReport",
        permission: "allow",
        can: true,
        path: "/reports/account/bankReconciliation",
        icon: <AccountBalanceIcon />,
        description: "Helps reconcile bank statement with your accounting records."
      },
      {
        label: "Balance Sheet Report",
        name: "balanceSheetReport",
        permission: "allow",
        can: true,
        path: "/reports/account/balanceSheet",
        icon: <TableChartIcon />,
        description: "Provides a snapshot of assets, liabilities, and equity at a point in time."
      },
      {
        label: "Profit Loss Report",
        name: "profitLossReport",
        permission: "allow",
        can: true,
        path: "/reports/account/profitLoss",
        icon: <BubbleChartIcon />,
        description: "Summarizes income and expenses to show net profit or loss."
      },
      {
        label: "Daybook Report",
        name: "dayBookReport",
        permission: "allow",
        can: true,
        path: "/reports/account/dayBookReport",
        icon: <LibraryBooksIcon />,
        description: "Chronological listing of all accounting entries for a given day or period."
      }
    ],
    stock: [
      {
        label: "Stock Audit Report",
        name: "stockAuditReport",
        permission: "report:stockAuditReport",
        can: useCan("report:stockAuditReport", {}),
        path: "/reports/stock/stockAuditReport",
        icon: <VerifiedUserIcon />,
        description: "Audits stock levels by comparing recorded and actual quantities."
      },
      {
        label: "Stock Cash Flow Report",
        name: "stockCashFlowReport",
        permission: "report:stockCashFlowReport",
        can: useCan("report:stockCashFlowReport", {}),
        path: "/reports/stock/stockCashFlowReport",
        icon: <EuroIcon />,
        description: "Tracks the cash flow impact of stock purchases and sales."
      },
      {
        label: "Supplier Ledger Report",
        name: "supplierLedgerReport",
        permission: "report:supplierLedgerReport",
        can: useCan("report:supplierLedgerReport", {}),
        path: "/reports/stock/supplierLedgerReport",
        icon: <InsertChartIcon />,
        description: "Displays transaction history and balances with suppliers."
      },
      {
        label: "Stock Transactions Report",
        name: "stockTransactionsReport",
        permission: "report:stockAuditReport",
        can: useCan("report:stockAuditReport", {}),
        path: "/reports/stock/stockTransactions",
        icon: <InventoryIcon />,
        description: "Lists all inventory movements including purchases, sales, and adjustments."
      },
      {
        label: "Stock History Report",
        name: "stockHistoryReport",
        permission: "report:stockAuditReport",
        can: useCan("report:stockAuditReport", {}),
        path: "/reports/stock/stockHistory",
        icon: <HistoryIcon />,
        description: "Shows historical changes in stock quantities and values over time."
      },
      {
        label: "Stock Summary Report",
        name: "stockSummaryReport",
        permission: "report:stockAuditReport",
        can: useCan("report:stockAuditReport", {}),
        path: "/reports/stock/stockSummary",
        icon: <SummarizeIcon />,
        description: "Provides a high-level overview of current stock status across items."
      },
      {
        label: "Stock Expiry Report",
        name: "stockExpiryReport",
        permission: "allow",
        can: true,
        path: "/reports/stock/stockExpiry",
        icon: <GppMaybeIcon />,
        description: "Highlights items that are nearing or have passed their expiration date."
      },
      {
        label: "Stock Ledger Report",
        name: "stockLedgerReport",
        permission: "allow",
        can: true,
        path: "/reports/stock/stockLedger",
        icon: <TextSnippetIcon />,
        description: "Detailed ledger of stock inflows and outflows for each item."
      }
    ],
    message: [
      {
        label: "Message Report",
        name: "messageReport",
        permission: "report:smsReport",
        can: useCan("report:smsReport", {}),
        path: "/reports/messageReport",
        icon: <SmsFailedIcon />,
        description: "Overview of all messages sent and received."
      }
    ],
    daybook: [
      {
        label: "Daybook Report",
        name: "dayBookReport",
        permission: "allow",
        can: true,
        path: "/reports/dayBookReport",
        icon: <MenuBook />,
        description: "A detailed report of daily transactions in the daybook."
      }
    ],
    lab: [
      {
        label: "Lab Report",
        name: "labReport",
        permission: "lab:listLab",
        can: useCan("lab:listLab", {}),
        path: "/reports/labReport",
        icon: <FlaskIcon />,
        description: "Overview of lab tests and results."
      }
    ],
    medical: [
      {
        label: "Medical Report",
        name: "medicalReport",
        permission: "lab:listLab",
        can: useCan("lab:listLab", {}),
        path: "/reports/labReport",
        icon: <FlaskIcon />,
        description: "Overview of medical tests and results."
      }
    ],
    calendar: [
      {
        label: "Appointment Report",
        name: "appointmentReport",
        permission: "report:patientFlowReport",
        can: useCan("report:patientFlowReport", {}),
        path: "/reports/patient/appointmentReport",
        icon: <TableChartIcon />,
        description: "Overview of patient appointments and flow."
      },
      {
        label: "Reminder Report",
        name: "reminderReport",
        permission: "report:patientFlowReport",
        can: useCan("report:patientFlowReport", {}),
        path: "/reports/patient/reminders?module=booking",
        icon: <TodayIcon />,
        description: "Details of patient reminders and follow-ups."
      }
    ],
    opd: [
      {
        label: "Patient Prescription Report",
        name: "patientPrescriptionReport",
        permission: "report:patientAssessmentReport",
        can: useCan("report:patientAssessmentReport", {}),
        path: "/reports/patient/patientAssessmentReport",
        icon: <BubbleChartIcon />,
        description: "Overview of patient prescriptions and assessments."
      },
      {
        label: "Reminder Report",
        name: "reminderReport",
        permission: "report:patientFlowReport",
        can: useCan("report:patientFlowReport", {}),
        path: "/reports/patient/reminders?module=assessment",
        icon: <TodayIcon />,
        description: "Details of patient reminders and follow-ups."
      },
      {
        label: "Dental Lab Referral Report",
        name: "dentalLabReferralReport",
        permission: "allow",
        can: true,
        path: "/reports/otherReport/labReferralReport",
        icon: <ScienceIcon />,
        description: "Overview of dental lab referrals and results."
      }
    ],
    hmis: [
      {
        label: "HMIS Main Report",
        name: "hmisMainReport",
        permission: "allow",
        can: useCan("allow", {}),
        path: "/reports/otherReport/hmislab",
        icon: <SummarizeIcon />,
        description: "Overview of HMIS data and reports."
      },
      {
        label: "HMIS Lab Report",
        name: "hmisLabReport",
        permission: "report:labReport",
        can: useCan("report:labReport", {}),
        path: "/reports/otherReport/hmislab",
        icon: <SummarizeIcon />,
        description: "Overview of lab tests and results."
      }
    ],
    client: [
      {
        label: "Client Report",
        permission: "report:patientFlowReport",
        can: useCan("report:patientFlowReport", {}),
        path: "/reports/patient/clientReport",
        icon: <PeopleIcon />,
        description: "Overview of client interactions, visits and history."
      }
    ]
  };

  return reports;
}
